import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  MouseEvent,
} from 'react';
import { useSearch } from '../../../../SearchContext';
import { useCardsList } from '~/hooks/useCardsList';
import { useTableViewManager } from '~/context/tableViewManager';
import { ActionButtonsContainer, Dropdown, DropdownItem } from './styles';
import { getFiltrosCard } from '~/components/Card/service/api';
import { FilterContext } from '~/context/filter';
import { FaEdit } from 'react-icons/fa';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';

interface ActionButtonsProps {
  disabled?: boolean;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  disabled = false,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [cardLoadedFiltersCount, setCardLoadedFiltersCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    removeAllFilters,
    filtersDaTela,
    setIsOpenModal,
    setIdFiltro,
    getValues,
    setValue,
    codTela,
    tipoAcesso,
    queryClient,
    setDataEdit,
    dataEdit,
    setIsCardFixed,
    setUpdateCard,
    setShowTable,
    refetch,
    isOpenModal,
    setSearchBarValue,
    selectedCard,
    setSelectedCard,
    isCardModified,
    setIsCardModified,
  } = useSearch();

  const { AddFilterOrUpdate, RemoveFilterisBlock } = useContext(FilterContext);
  const { cards, refetchCards } = useCardsList(codTela, tipoAcesso);
  const { onChangeCodCardSelected, findViewByCardClicked, viewCardSelected } =
    useTableViewManager();

  const shouldClearFilter = getValues('shouldClearFilter');

  // Filtra apenas os filtros visíveis (não ocultos)
  const visibleFilters = filtersDaTela.filter((filter) => !filter.isHidden);

  // Refazer busca pelos cards quando modal fechar e card foi salvo/atualizado
  useEffect(() => {
    if (!isOpenModal) {
      // Aguardar um pouco para garantir que a operação do card foi concluída
      const timer = setTimeout(async () => {
        await refetchCards();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isOpenModal, refetchCards]);

  // Selecionar/atualizar automaticamente o card após salvamento
  useEffect(() => {
    if (
      dataEdit &&
      dataEdit.cod_card &&
      dataEdit.cod_card > 0 &&
      !isOpenModal
    ) {
      setSelectedCard((prev: any) => {
        // Se já há um card selecionado e é o mesmo que foi editado, atualizar seus dados
        if (prev && prev.cod_card === dataEdit.cod_card) {
          return {
            cod_card: dataEdit.cod_card,
            des_card: dataEdit.des_card,
            cor: dataEdit.cor,
            cor_texto: prev.cor_texto || '#FFFFFF',
          };
        }
        // Se não há card selecionado, selecionar o card editado
        if (!prev) {
          return {
            cod_card: dataEdit.cod_card,
            des_card: dataEdit.des_card,
            cor: dataEdit.cor,
            cor_texto: '#FFFFFF', // Cor padrão do texto
          };
        }
        // Caso contrário, manter o card atual
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataEdit, isOpenModal]);

  // Limpar card selecionado quando um card é excluído
  useEffect(() => {
    if (!viewCardSelected) {
      setSelectedCard(null);
      setIsCardModified(false);
      setCardLoadedFiltersCount(0);

      // Executar busca sem filtros
      if (refetch) {
        refetch();
      }
    }
  }, [viewCardSelected, refetch, setSelectedCard, setIsCardModified]);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Detectar quando filtros são alterados após selecionar um card
  useEffect(() => {
    if (selectedCard && cardLoadedFiltersCount > 0) {
      // Se há diferença na quantidade de filtros, marca como modificado
      if (filtersDaTela.length !== cardLoadedFiltersCount) {
        setIsCardModified(true);
      } else {
        setIsCardModified(false);
      }
    }
  }, [filtersDaTela, selectedCard, cardLoadedFiltersCount, setIsCardModified]);

  useEffect(() => {
    if (disabled) {
      setShowDropdown(false);
    }
  }, [disabled]);

  const handleMinhasPesquisas = () => {
    if (disabled) return;
    setShowDropdown(!showDropdown);
  };

  const handleLimparFiltros = () => {
    if (disabled) return;
    if (shouldClearFilter) {
      removeAllFilters();
      setValue('shouldClearFilter', false);
      setSelectedCard(null); // Limpar o card selecionado
      setIsCardModified(false); // Resetar estado de modificação
      setCardLoadedFiltersCount(0); // Resetar contagem base
      setUpdateCard(false); // Resetar estado de update do card
      setDataEdit({
        cod_card: 0,
        des_card: '',
        cor: '',
      }); // Limpar dados de edição
      setSearchBarValue('');

      // Limpar cache do card ativo
      queryClient.removeQueries([`infoCardAtivo_${codTela}`]);

      // Resetar seleção de card na tabela
      onChangeCodCardSelected(null);
    }
  };

  const handleSalvarPesquisa = () => {
    if (disabled) return;
    if (visibleFilters.length > 0) {
      setIdFiltro('');
      setIsOpenModal(true);
    }
  };

  const handleEditCard = (card: any, event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (disabled) return;

    // Configurar o card para edição
    setDataEdit({
      cod_card: card.cod_card,
      des_card: card.des_card,
      cor: card.cor,
    });

    setIsCardFixed(card.cod_card_fixado === card.cod_card);
    setUpdateCard(true);

    // Abrir modal de edição
    setIsOpenModal(true);
    setShowDropdown(false);
  };

  const handleCampoClick = async (card: any) => {
    if (disabled) return;

    // Se clicar no mesmo card já selecionado, não fazer nada
    if (selectedCard && selectedCard.cod_card === card.cod_card) {
      setShowDropdown(false);
      return;
    }

    // Se há um card selecionado diferente, limpar filtros existentes primeiro
    if (selectedCard) {
      RemoveFilterisBlock(codTela);
      setValue('shouldClearFilter', false);
      setIsCardModified(false);
      setCardLoadedFiltersCount(0);
      setUpdateCard(false);
      setDataEdit({
        cod_card: 0,
        des_card: '',
        cor: '',
      });
      setSearchBarValue('');

      // Limpar cache do card ativo
      queryClient.removeQueries([`infoCardAtivo_${codTela}`]);

      // Resetar seleção de card na tabela
      onChangeCodCardSelected(null);
    }

    // Definir o novo card selecionado
    setSelectedCard(card);
    setIsCardModified(false);
    setCardLoadedFiltersCount(0);

    // Configurar o card ativo no cache
    const infoCard = {
      cod_card: card.cod_card,
      des_card: card.des_card,
      cor: card.cor,
      cor_texto: card.cor_texto,
      cod_card_fixado: card.cod_card_fixado,
      tipo_acesso: card.tipo_acesso,
      cod_usuario: card.cod_usuario,
    };

    // Usar as funções do CardContainer para executar o card
    onChangeCodCardSelected(card.cod_card);
    await findViewByCardClicked(card.cod_card, codTela);

    queryClient.setQueryData(`infoCardAtivo_${codTela}`, infoCard);

    // Carregar filtros do card
    try {
      const filtrosCard = await getFiltrosCard(card.cod_card);

      if (
        filtrosCard.success &&
        filtrosCard.data &&
        filtrosCard.data.length > 0
      ) {
        // Converter os filtros do card para o formato esperado
        const newFilters = filtrosCard.data.map((filtro: any) => {
          // Verificar se des_valor é uma string JSON e fazer parse se necessário
          let desValor = filtro.des_valor;
          if (
            typeof desValor === 'string' &&
            desValor.startsWith('[') &&
            desValor.endsWith(']')
          ) {
            try {
              desValor = JSON.parse(desValor);
            } catch (error) {
              console.error('Erro ao fazer parse do des_valor:', error);
              // Manter o valor original se não conseguir fazer parse
              desValor = filtro.des_valor;
            }
          }

          // Para pesquisas salvas que contenham macro, vai formatar para number corretamente
          if (typeof desValor === 'string' && filtro.cod_tipo_info) {
            const cleaned = desValor.replace(/\D/g, '');
            desValor = cleaned ? parseInt(cleaned, 10) : null;
          }

          return {
            index: `${filtro.cod_campo}_${Date.now()}`, // Gerar um index único
            cod_campo: filtro.cod_campo,
            des_nome: filtro.des_campo || filtro.des_nome,
            nome_bd: filtro.nome_bd,
            condicao: filtro.condicao,
            des_valor: desValor,
            cod_tipo_dados: filtro.cod_tipo_dados,
            cod_tipo_info: filtro.cod_tipo_info,
            isBlock: false,
            expressao_validacao: filtro.expressao_validacao,
            listagem_info: filtro.listagem_info,
            tipo_expressao: filtro.tipo_expressao,
          };
        });

        // Adicionar os filtros ao contexto
        AddFilterOrUpdate({
          codTela,
          edit: true,
          flg_pesquisado: false,
          name_card: card.des_card,
          cor: card.cor,
          itens: newFilters,
        });

        setValue('shouldClearFilter', true);
        // Definir a contagem base dos filtros do card após carregá-los
        setCardLoadedFiltersCount(newFilters.length);

        // Resetar estado modificado já que acabamos de carregar os filtros do card
        setIsCardModified(false);

        // Executar a busca apenas após aplicar os filtros
        refetch();
      } else {
        // Se não há filtros no card, apenas definir shouldClearFilter como true
        setValue('shouldClearFilter', true);
        setCardLoadedFiltersCount(0);
        setIsCardModified(false);
      }
    } catch (error) {
      console.error('Erro ao carregar filtros do card:', error);
      // Mesmo com erro, definir shouldClearFilter como true
      setValue('shouldClearFilter', true);
      setCardLoadedFiltersCount(0);
      setIsCardModified(false);
    }

    setDataEdit({
      cod_card: card.cod_card,
      des_card: card.des_card,
      cor: card.cor,
    });

    setIsCardFixed(card.cod_card_fixado === card.cod_card);
    setUpdateCard(true);
    setShowTable(true);
    setShowDropdown(false);
  };

  return (
    <ActionButtonsContainer>
      <div className="action-buttons">
        <div className="dropdown-container" ref={dropdownRef}>
          <button
            type="button"
            className="action-button"
            onClick={handleMinhasPesquisas}
            disabled={disabled}
          >
            Minhas pesquisas
            {showDropdown ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
          </button>
          {showDropdown && (
            <Dropdown>
              {cards?.length > 0 ? (
                cards.map((card: any) => (
                  <DropdownItem
                    key={card.cod_card}
                    onClick={() => handleCampoClick(card)}
                    title={card.des_card}
                    cor={card.cor}
                    corTexto={card.cor_texto}
                  >
                    <div className="card-content">
                      <span className="campo-nome">{card.des_card}</span>
                      {card.usuario && (
                        <span className="campo-sinonimo">
                          Por: {card.usuario}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      className="edit-button"
                      onClick={(event) => handleEditCard(card, event)}
                      title="Editar pesquisa"
                      disabled={disabled}
                    >
                      <FaEdit />
                    </button>
                  </DropdownItem>
                ))
              ) : (
                <DropdownItem disabled>
                  Nenhuma busca personalizada encontrada
                </DropdownItem>
              )}
            </Dropdown>
          )}
        </div>

        {selectedCard && (
          <div
            className="selected-card"
            style={{
              backgroundColor: selectedCard.cor,
              color: selectedCard.cor_texto,
            }}
          >
            {selectedCard.des_card}
            {isCardModified && ' *'}
          </div>
        )}

        <button
          type="button"
          className="action-button"
          onClick={handleLimparFiltros}
          disabled={disabled || !shouldClearFilter}
        >
          Limpar filtros
        </button>

        <button
          type="button"
          className="action-button"
          onClick={handleSalvarPesquisa}
          disabled={disabled || !(visibleFilters.length > 0)}
          style={{
            color:
              disabled || !(visibleFilters.length > 0) ? undefined : '#1371bf',
          }}
        >
          Salvar Pesquisa
        </button>
      </div>
    </ActionButtonsContainer>
  );
};
